/**
 * Pure view helpers for R1 W2's applied-edit notice and per-document
 * suggestion list (kept out of the component files for fast refresh).
 */

import { listSectionNames } from '@/lib/pending-ai-change-notices'
import { SUGGESTIONS_WAITING_MANY, SUGGESTIONS_WAITING_ONE, fillCopy } from '@/lib/ai-edit-copy'
import type { AppliedAiEdit } from '@/stores/ai-edits-store'

export interface PendingSuggestionItem {
  proposalId: string
  sectionId: string
  heading: string
}

/**
 * The newest edit to a section on screen that the user has not dismissed.
 * Matched by segment id; an edit without one falls back to its section key.
 */
export function pickEditorEdit(
  edits: ReadonlyArray<AppliedAiEdit>,
  visibleSegments: ReadonlyArray<{ id: string; sectionId: string }>,
): AppliedAiEdit | null {
  const ids = new Set(visibleSegments.map((segment) => segment.id))
  const keys = new Set(visibleSegments.map((segment) => segment.sectionId))
  for (let index = edits.length - 1; index >= 0; index -= 1) {
    const edit = edits[index]!
    if (edit.dismissed) continue
    const onScreen = edit.segmentId ? ids.has(edit.segmentId) : keys.has(edit.sectionId)
    if (onScreen) return edit
  }
  return null
}

export function suggestionsSummary(items: ReadonlyArray<PendingSuggestionItem>): string {
  const count = items.length === 1
    ? SUGGESTIONS_WAITING_ONE
    : fillCopy(SUGGESTIONS_WAITING_MANY, { count: String(items.length) })
  const names = listSectionNames(items.map((item) => item.heading))
  return names ? `${count} · ${names}` : count
}

/**
 * Where a section's editor lives, keeping the editor family the user is in:
 * a questionnaire stays on the questionnaire editor, everything else opens the
 * section editor.
 */
export function suggestionSectionPath(
  pathname: string,
  bidId: string,
  artifactId: string,
  sectionKey: string,
): string {
  const key = encodeURIComponent(sectionKey)
  if (pathname.includes(`/responses/${artifactId}/questionnaire`)) {
    return `/app/bids/${bidId}/responses/${artifactId}/questionnaire/${key}`
  }
  return `/app/bids/${bidId}/responses/${artifactId}/sections/${key}`
}
